import { InventoryTransactionType, ProductInventoryDoc, ProductInventoryUnit } from '@gooduncles/gu-app-schema';
import { ICellEditorParams } from 'ag-grid-community';
import { Button, Card, DatePicker, Form, Input, InputNumber, Radio, Select, message } from 'antd';
import { Dayjs } from 'dayjs';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useAuth } from 'src/stores/auth-context';
import { formatDate, getDefaultDate } from 'src/utils/date-util';
import { callInventoryIssueTransaction } from 'src/utils/firebase/firebase-callable';
import { sortUomUnit } from 'src/utils/product-inventory-util';

interface AgCellInventoryIssueEditorProps extends ICellEditorParams {
  inventoryData: ProductInventoryDoc;
}

interface InventoryIssueFormValues {
  date: Dayjs;
  inventoryUnit: ProductInventoryUnit;
  transactionType: InventoryTransactionType;
  requestValue: number | null;
  reason: string;
}

const defaultIssueDate = getDefaultDate();

const AgCellInventoryIssueEditor = forwardRef<any, AgCellInventoryIssueEditorProps>(
  ({ inventoryData, stopEditing }, ref) => {
    const { user } = useAuth();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [originalQuantity] = useState<number>(inventoryData.quantity);

    const initialValues: InventoryIssueFormValues = {
      date: defaultIssueDate,
      inventoryUnit: Object.keys(inventoryData.uom || {}).includes(ProductInventoryUnit.EA)
        ? ProductInventoryUnit.EA
        : (Object.keys(inventoryData.uom || {})[0] as ProductInventoryUnit),
      transactionType: InventoryTransactionType.INBOUND,
      requestValue: 1,
      reason: '',
    };

    // Ag-Grid에서 cellEditor에서 변경된 값을 접근하기 위해 사용
    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return originalQuantity; // 값은 변경하지 않고 원래 값 반환 (이벤트로 처리)
        },
      };
    });

    const unitOptions = sortUomUnit(Object.keys(inventoryData.uom || {})).map((unit) => ({
      label: unit,
      value: unit,
    }));

    const validateInventoryIssueForm = (
      values: InventoryIssueFormValues
    ): { isValid: boolean; errorMessage?: string } => {
      const { date, inventoryUnit, requestValue, reason, transactionType } = values;

      // 날짜 검증
      if (!date) {
        return { isValid: false, errorMessage: '이슈 날짜를 선택해주세요.' };
      }

      // 미래 날짜 검증
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = date.toDate();
      selectedDate.setHours(0, 0, 0, 0);
      if (selectedDate > today) {
        return { isValid: false, errorMessage: '미래 날짜는 선택할 수 없습니다.' };
      }

      // 단위 검증
      if (!inventoryUnit) {
        return { isValid: false, errorMessage: '단위를 선택해주세요.' };
      }

      // 수량 검증
      if (!requestValue || requestValue <= 0) {
        return { isValid: false, errorMessage: '수량은 1 이상이어야 합니다.' };
      }

      // 이슈 내용 검증
      if (!reason || reason.trim() === '') {
        return { isValid: false, errorMessage: '이슈 내용을 입력해주세요.' };
      }

      // 이슈 내용 길이 검증
      if (reason.length > 100) {
        return { isValid: false, errorMessage: '이슈 내용은 100자 이내로 입력해주세요.' };
      }

      // 출고 무제한 검증
      if (transactionType === InventoryTransactionType.OUTBOUND && requestValue === Infinity) {
        return { isValid: false, errorMessage: '출고 무제한은 입력할 수 없습니다.' };
      }

      return { isValid: true };
    };

    const handleSubmit = async (values: InventoryIssueFormValues) => {
      if (!user) {
        message.error('로그인 후 이용해주세요.');
        return;
      }

      // 폼 데이터 검증
      const validation = validateInventoryIssueForm(values);
      if (!validation.isValid) {
        message.error(validation.errorMessage);
        return;
      }

      setLoading(true);
      try {
        const { date, inventoryUnit, transactionType, requestValue, reason } = values;
        const transactionTypeText = transactionType === InventoryTransactionType.INBOUND ? '입고' : '출고';
        const issueDate = formatDate(date.toDate(), 'yyyy-MM-dd');

        const res = await callInventoryIssueTransaction({
          body: {
            issueDate,
            inventoryId: inventoryData._id,
            inventoryUnit,
            transactionType,
            requestValue: requestValue === Infinity ? null : requestValue,
            reason,
          },
        });

        if (res.data.result === 'success') {
          message.success(`${transactionTypeText} 이슈 요청 전송 완료.`);
          stopEditing();
        } else {
          message.error(`${transactionTypeText} 이슈 요청 실패. error: ${res.data.reason}`);
        }
      } catch (error) {
        console.error('재고 이슈 생성 오류:', error);
        message.error('재고 이슈 생성에 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    const handleCancel = () => {
      stopEditing();
    };

    return (
      <Card
        title='재고 수량 변경'
        extra={<Button onClick={handleCancel}>닫기</Button>}
        style={{ width: 650, padding: 0 }}>
        <Form
          form={form}
          layout='vertical'
          onFinish={handleSubmit}
          initialValues={initialValues}
          style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '0 16px 16px' }}>
          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name='date'
              label='이슈 날짜'
              style={{ flex: 1 }}
              rules={[{ required: true, message: '이슈 날짜를 선택해주세요' }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item label='재고' style={{ flex: 2 }}>
              <Input value={inventoryData.inventoryName} disabled />
            </Form.Item>

            <Form.Item
              name='inventoryUnit'
              label='단위'
              style={{ flex: 1 }}
              rules={[{ required: true, message: '단위을 선택해주세요' }]}>
              <Select placeholder='단위 선택' options={unitOptions} />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
            <Form.Item label='현 재고량' style={{ width: 140 }}>
              <b>{inventoryData.quantity === Infinity ? '무제한' : `${inventoryData.quantity}`}</b>
            </Form.Item>

            <Form.Item
              name='requestValue'
              label='수량'
              style={{ flex: 1 }}
              rules={[{ required: true, message: '수량을 입력해주세요' }]}>
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                addonAfter={
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => {
                      if (prevValues.transactionType !== currentValues.transactionType) {
                        // 출고로 변경되고 현재 값이 무제한이면 0으로 변경
                        if (
                          currentValues.transactionType === InventoryTransactionType.OUTBOUND &&
                          form.getFieldValue('requestValue') === Infinity
                        ) {
                          form.setFieldsValue({ requestValue: 0 });
                        }
                        return true;
                      }
                      return false;
                    }}>
                    {({ getFieldValue }) => (
                      <Button
                        size='small'
                        type='text'
                        onClick={() => form.setFieldsValue({ requestValue: Infinity })}
                        disabled={getFieldValue('transactionType') === InventoryTransactionType.OUTBOUND}
                        style={{ padding: '0 8px' }}>
                        무제한
                      </Button>
                    )}
                  </Form.Item>
                }
              />
            </Form.Item>

            <Form.Item name='transactionType' label='입출고 유형'>
              <Radio.Group>
                <Radio.Button value={InventoryTransactionType.INBOUND}>입고</Radio.Button>
                <Radio.Button value={InventoryTransactionType.OUTBOUND}>출고</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </div>

          <Form.Item
            name='reason'
            label='이슈 내용'
            style={{ flex: 1 }}
            rules={[{ required: true, message: '이슈 내용을 입력해주세요' }]}>
            <Input placeholder='이슈 내용을 입력하세요' />
          </Form.Item>

          <Form.Item
            shouldUpdate={(prevValues, currentValues) => prevValues.transactionType !== currentValues.transactionType}>
            {({ getFieldValue }) => (
              <Button
                type='primary'
                danger={getFieldValue('transactionType') === InventoryTransactionType.OUTBOUND}
                htmlType='submit'
                loading={loading}>
                {getFieldValue('transactionType') === InventoryTransactionType.INBOUND ? '입고 처리' : '출고 처리'}
              </Button>
            )}
          </Form.Item>
        </Form>
      </Card>
    );
  }
);

AgCellInventoryIssueEditor.displayName = 'AgCellInventoryIssueEditor';

export default AgCellInventoryIssueEditor;
